import {
  computeStage,
  FIELD_HIDDEN_JOB_CARDS,
  isFieldView,
  jobAddress,
  nothingToSettle,
  partnersEnabled,
  paymentTermsWarning,
  primaryPhone,
  slotForSubmittedBox,
  tickSettledNothing,
  unsignedWorkTask,
  unsignedWorkTaskKey,
  type Job,
  type JobChecklistItem,
  type PaymentSlot,
  type RoofLayout,
  type WallMeasurement,
} from "@roof-hawk/core";
import { useCallback, useEffect, useRef, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { WorkingOnNotice } from "../../components/WorkingOnNotice";
import { CallButton } from "../phone/CallButton";
import { t } from "../../i18n";
import { fetchCompanycamLink, pushCompanycamQueue } from "../../lib/companycam";
import {
  fetchConversationQuestions,
  fetchConversations,
} from "../../lib/conversations";
import { navigate, useAsync, useRememberedState } from "../../lib/hooks";
import { useCardFromLink } from "../../lib/jobLink";
import { useWorkingOn } from "../../lib/useWorkingOn";
import {
  type CustomerPaymentRow,
  fetchCustomerPayments,
  fetchPaymentHistory,
  markLinkInTheBank,
  markPaymentInTheBank,
  removeCustomerPayment,
  markPlanPaymentBanked,
  sayCheckIsComing,
  turnPaymentRemindersOff,
  setChecklistItemStatus,
} from "../../lib/payments";
import {
  deleteRow,
  fetchChecklist,
  fetchCompanySettings,
  fetchJob,
  fetchJobBalance,
  fetchJobPlans,
  fetchUnsignedChangeOrderWarning,
  fetchUnsignedWork,
  fetchDismissedTaskKeys,
  answerDerivedTask,
  fetchPaymentTermsCheck,
  dismissPaymentTermsCheck,
  fetchLinksStillLive,
  fetchJobWorkOrders,
  fetchMercuryForJob,
  fetchRoofLayouts,
  fetchWallMeasurements,
  insertRow,
  recordDepositAmount,
  updateRow,
  registerJobFile,
  uploadRoofReport,
  uploadSignedContract,
} from "../../lib/queries";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";
import { EstimateCard } from "../estimate/EstimateCard";
import type { AppData } from "../shell/Shell";
import { ActivityPanel } from "./ActivityPanel";
import { CompanyCamCard } from "./CompanyCamCard";
import { ConversationsCard } from "./ConversationsCard";
import {
  ChecklistPanel,
  type SignedContractInput,
  type VerifyValues,
} from "./ChecklistPanel";
import { EmailPanel } from "./EmailPanel";
import { TextsPanel } from "./TextsPanel";
import type { RoofLayoutInput } from "./RoofMeasurementModal";
import type { WallMeasurementInput } from "./WallMeasurementModal";
import { WallMeasurementsCard } from "./WallMeasurementsCard";
import { DocsPanel } from "./DocsPanel";
import { NotesPanel } from "./NotesPanel";
import { BalanceCard } from "../../components/BalanceCard";
import { OverviewPanel } from "./OverviewPanel";
import { PartnersPanel } from "./PartnersPanel";
import { MaterialsCheckWarning } from "./MaterialsCheckWarning";
import { StormReportPanel } from "./StormReportPanel";
import { InsuranceEstimateCard } from "../insurance/InsuranceEstimateCard";
import { RoofMeasurementsCard } from "./RoofMeasurementsCard";
import { PaymentLinkModal } from "./PaymentLinkModal";
import { PublicLeadMatchReview } from "./PublicLeadMatchReview";
import { ReceiptReviewModal } from "./ReceiptReviewModal";
import { PaymentsCard } from "./PaymentsCard";
import { RecordPaymentModal } from "./RecordPaymentModal";

/**
 * The cards across the top of a job. Only some are built.
 *
 * The unbuilt ones are kept here rather than deleted — they are the agreed
 * shape of the job screen and each will be filled in — but they are not drawn.
 * Six of the fourteen answered a tap with "Planned, not wired up in v1.", and
 * on a phone the grid of them took nearly a third of the screen before any
 * content: the rep paid for the roadmap on every visit to every job.
 *
 * `PLANNED` is what brings one back. Set it to true and the full grid returns,
 * stubs and all, which is what makes this a switch rather than a deletion.
 */
export const CARDS: {
  name: string;
  live?: boolean;
  stub?: string;
  /** Shown only when the job is an insurance bid. */
  insuranceOnly?: boolean;
  /** Shown only while the partner network is switched on (ADR 0144). */
  partnersOnly?: boolean;
}[] = [
  { name: "Overview", live: true },
  { name: "Estimate", live: true },
  // Every payment the customer has made, and the door to enter one by hand
  // (ADR 0288).
  { name: "Payments", live: true },
  // Insurance only. `insuranceOnly` is filtered out on every other bid type,
  // which is the toggle Jonathon asked for: a retail job never grows a tab
  // for a document it will never send.
  { name: "Insurance Estimate", live: true, insuranceOnly: true },
  { name: "Notes", live: true },
  { name: "Activity", live: true },
  { name: "Invoices" },
  { name: "Commissions" },
  { name: "Events" },
  { name: "Docs", live: true },
  { name: "Photos", live: true },
  { name: "Conversations", live: true },
  { name: "Emails", live: true },
  { name: "Texts", live: true },
  // Who sent us this one, and who we could send them to. Asked here because a
  // referral nobody records on the day is a partner who never finds out their
  // introduction turned into a roof (ADR 0067). Off since 2026-09-14 and not
  // drawn on any job — `partnersOnly`, ADR 0144.
  { name: "Partners", live: true, partnersOnly: true },
  {
    // Drawing a roof *inside* Roof Board. Nothing to do with the Draw Roof
    // step and the Measure the roof button, which hand the address over so the
    // rep can draw it in iRoofing or order it from QuickMeasure (ADR 0091,
    // ADR 0109).
    name: "Roof Drawing",
    stub: "Drawing a roof in Roof Board is not built. The Draw Roof step, and Measure the roof on the Roof Measurements card, hand the address across to iRoofing or QuickMeasure.",
  },
  { name: "Roof Measurements", live: true },
  { name: "Inspections" },
  { name: "Todo" },
];

/** Show the cards that are not built yet. Off: they are noise on a phone. */
export const PLANNED = false;

/**
 * The cards this job actually draws, in the order above.
 *
 * Three filters, and they are the same idea three times. A tab that is visible
 * and refuses to open is a question the rep answers on every visit — whether
 * it refuses because the feature is not built, because this job is not an
 * insurance bid and never will have a claim sheet, or because the company does
 * not work with referral partners.
 *
 * A fourth, for a field login (ADR 0322): the cards Jonathon said Carlos does
 * not need are not drawn for him. Hidden, not locked.
 */
export function shownCards(
  job: Pick<Job, "bid_type">,
  /** `company_settings.partners_enabled`. Unknown reads as off (ADR 0144). */
  partnersOn = false,
  /** The viewer is a field login (`profiles.field_view`). */
  fieldView = false,
) {
  return CARDS.filter(
    (c) =>
      (PLANNED || c.live) &&
      (!c.insuranceOnly || job.bid_type === "insurance") &&
      (!c.partnersOnly || partnersOn) &&
      (!fieldView || !FIELD_HIDDEN_JOB_CARDS.includes(c.name)),
  );
}

export function JobDetail({
  jobId,
  data,
  card: askedCard = null,
}: {
  jobId: string;
  data: AppData;
  /** A card the link asked for, which beats the remembered one. */
  card?: string | null;
}) {
  const { profile, profileId, displayName, people, isAdmin } = useSession();
  /**
   * Which tab of the job is open, kept across a refresh.
   *
   * It survives moving between jobs too, which it always did — opening the
   * next job on the card you were reading is the point of the tabs. What is
   * new is that pressing refresh no longer throws you back to Overview, and
   * that a card this job does not have falls back to Overview rather than
   * drawing an empty screen: an insurance tab remembered from an insurance job
   * is not a tab on a retail one.
   */
  const [rememberedCard, setCard] = useRememberedState(
    "job-card",
    "Overview",
    askedCard,
  );
  useCardFromLink(jobId, askedCard, setCard);
  // Bumped when the checklist sends the user to the estimate. It keys the card
  // so a fresh one mounts and offers the create choice again — otherwise
  // pressing Estimate a second time would appear to do nothing.
  const [estimateVisit, setEstimateVisit] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** The job whose CompanyCam queue this visit has already nudged. */
  const nudged = useRef<string | null>(null);

  const jobQuery = useAsync(() => fetchJob(jobId), [jobId]);
  // Announce this job as open, and hear who else has the customer open
  // (ADR 0268). Keyed on the customer, so a colleague inside another of their
  // jobs, or on the customer file, counts.
  const othersHere = useWorkingOn(
    jobQuery.data?.contact_id,
    { profileId, name: displayName },
    { jobId, jobNumber: jobQuery.data?.job_number ?? null },
  );
  const checklistQuery = useAsync(() => fetchChecklist(jobId), [jobId]);
  const companycamQuery = useAsync(() => fetchCompanycamLink(jobId), [jobId]);
  const conversationsQuery = useAsync(() => fetchConversations(jobId), [jobId]);
  const askedQuery = useAsync(() => fetchConversationQuestions(jobId), [jobId]);
  const layoutsQuery = useAsync(() => fetchRoofLayouts(jobId), [jobId]);
  // The walls, for siding. Apart from the roof on purpose (siding.ts).
  const wallsQuery = useAsync(() => fetchWallMeasurements(jobId), [jobId]);
  /* Only for `partners_enabled` — the Partners card is off (ADR 0144). */
  const companyQuery = useAsync(fetchCompanySettings, []);
  const workOrdersQuery = useAsync(() => fetchJobWorkOrders(jobId), [jobId]);
  /**
   * The work order the checklist wants opened, or "list" for the section.
   *
   * Jonathon, 2026-09-08: clicking All Work Complete "took me to the estimate
   * page but it wouldn't actually mark the work orders complete." It never
   * could — the box that moves is inside a work order, and the step only
   * changed tab. This is what carries the rest of the journey.
   */
  const [workOrderFocus, setWorkOrderFocus] = useState<string | null>(null);
  /**
   * What is still owed on this job.
   *
   * Fetched once here rather than inside each card, so the Overview and the
   * Estimate cannot show two different figures — which is the failure Jonathon
   * named on 2026-09-08. It reloads with the job, which is what makes it
   * current after a payment is matched on the ongoing board.
   */
  const balanceQuery = useAsync(
    async () => (jobQuery.data ? fetchJobBalance(jobQuery.data) : null),
    [jobQuery.data],
  );
  /**
   * How much of the balance above nobody has signed for.
   *
   * A change order counts towards the contract from the moment it is typed
   * (Jonathon, 2026-09-19), so the figure on the card can include work the
   * customer has never agreed to. This is the sentence that says so, and it
   * sits with the money rather than three screens away on the estimate.
   */
  const unsignedQuery = useAsync(
    () => fetchUnsignedChangeOrderWarning(jobId),
    [jobId],
  );
  /**
   * Work going ahead on an estimate nobody signed (issue #463): materials
   * ordered, a crew booked, or an invoice made. Warns, never blocks.
   * "Dismiss" hides one for this person until more happens on that estimate,
   * and ticks the same to-do off their list (ADR 0324).
   */
  const unsignedWorkQuery = useAsync(async () => {
    const found = await fetchUnsignedWork([jobId]);
    if (!profileId) return found;
    const dismissed = await fetchDismissedTaskKeys(
      profileId,
      found.map(unsignedWorkTaskKey),
    );
    return found.filter((one) => !dismissed.has(unsignedWorkTaskKey(one)));
  }, [jobId, profileId]);
  /**
   * Payment terms somebody promised that the contract does not show, as Jev
   * read the conversation (ADR 0226). Warns, never blocks; "This is fine"
   * hides it until the conversation or the paperwork changes.
   */
  const termsQuery = useAsync(() => fetchPaymentTermsCheck(jobId), [jobId]);
  const termsWarning = paymentTermsWarning(termsQuery.data);
  /**
   * Earlier payment links still live beside the final request (ADR 0139).
   * Read again whenever the balance is, which is when a request is raised.
   */
  const liveLinksQuery = useAsync(
    () => fetchLinksStillLive(jobId),
    [jobId, balanceQuery.data],
  );
  /** Checks and cash handed over on this job, with their receipts (ADR 0125). */
  const paymentsQuery = useAsync(() => fetchCustomerPayments(jobId), [jobId]);
  /** Every payment on the job, for the Payments tab (ADR 0288). */
  const historyQuery = useAsync(
    () => fetchPaymentHistory(jobId),
    [jobId, paymentsQuery.data],
  );
  /** The in-house payment plan, where the job has one (ADR 0286). */
  const planQuery = useAsync(
    async () => (await fetchJobPlans([jobId])).get(jobId) ?? null,
    [jobId, balanceQuery.data],
  );
  /**
   * Read once without error: until then nobody knows whether the Final is
   * offered. It stays known while the plan is read again: saving a payment
   * reloads the balance, which re-reads the plan, and gating on every read
   * unmounted the Record form mid-save, losing its receipt step and offering
   * the same check again (review of #933).
   */
  const [planReadFor, setPlanReadFor] = useState<string | null>(null);
  const planReadNow = planQuery.error === null && !planQuery.loading;
  useEffect(() => {
    if (planReadNow) setPlanReadFor(jobId);
  }, [planReadNow, jobId]);
  const planKnown = planReadNow || planReadFor === jobId;
  /** Recording one: the payment to start on, when a Submitted box said. */
  const [recording, setRecording] = useState<{
    slot?: PaymentSlot | null;
    clearedByBoxSlot?: PaymentSlot;
  } | null>(null);
  /**
   * The payment a tick of First or Final Check just settled nothing on, while
   * it is still owed (ADR 0182) — said, with the offer to record it here.
   */
  const [nothingSettled, setNothingSettled] = useState<PaymentSlot | null>(
    null,
  );
  /** The receipt somebody is reading over before it goes (ADR 0140). */
  const [reviewingReceipt, setReviewingReceipt] = useState<string | null>(null);
  /** Asking for an amount that is on no schedule (ADR 0141). */
  const [askingFor, setAskingFor] = useState(false);
  // What the customer already has a live link for, so a second link for the
  // same money can be warned about before it exists.
  const paymentRequests = useAsync(
    () => (askingFor ? fetchMercuryForJob(jobId) : Promise.resolve([])),
    [askingFor, jobId],
  );

  const job = jobQuery.data;
  const items = checklistQuery.data ?? [];
  const setChecklist = checklistQuery.setData;

  /** Reload the job and its checklist, then push changes into the lists. */
  const refresh = useCallback(async () => {
    await Promise.all([jobQuery.reload(), checklistQuery.reload()]);
    await data.reload();
  }, [jobQuery, checklistQuery, data]);

  /**
   * Keep the computed stage in step with the checklist, unless a user has
   * overridden it. The prototype did this on every refresh and so do we.
   */
  useEffect(() => {
    if (!job || job.stage_manual) return;
    if (checklistQuery.loading) return;
    const next = computeStage(items, job);
    if (next === job.stage) return;

    void (async () => {
      try {
        await updateRow("jobs", job.id, { stage: next });
        await insertRow("job_stage_events", {
          job_id: job.id,
          stage: next,
          entered_by: profileId,
        });
        jobQuery.setData({ ...job, stage: next });
        await data.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    job?.id,
    job?.stage,
    job?.stage_manual,
    job?.signed_contract_at,
    items,
    checklistQuery.loading,
  ]);

  /**
   * Opening a job that has no CompanyCam project asks the server to have
   * another go.
   *
   * This *is* the retry engine. There is no scheduler behind this integration
   * — Jonathon chose that on 2026-08-22 — so the retries ride on somebody
   * using the app, which in this shop is all day. Once per visit, and only
   * while the row is still waiting or failed: an abandoned one waits for the
   * button, deliberately, because five failures in a row is not something more
   * patience fixes.
   */
  useEffect(() => {
    if (!job) return;
    const link = companycamQuery.data;
    const waiting = link?.state === "pending" || link?.state === "failed";
    // A linked job comes back too, when its labels have gone out of date —
    // the bid type changed, or an estimate finally said what is going on the
    // roof (ADR 0057).
    const stale = link?.state === "linked" && link.needs_label_sync;

    // Nothing outstanding, so forget that this visit already asked. Staleness
    // recurs where a missing project does not: a rep who sets the bid type to
    // insurance and then back to retail has made the labels wrong twice in one
    // sitting, and the second time deserves its own nudge rather than being
    // swallowed by the first.
    if (!waiting && !stale) {
      if (nudged.current === job.id) nudged.current = null;
      return;
    }

    if (nudged.current === job.id) return;
    nudged.current = job.id;
    void (async () => {
      await pushCompanycamQueue();
      await Promise.all([jobQuery.reload(), companycamQuery.reload()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    job?.id,
    job?.companycam_project_id,
    companycamQuery.data?.state,
    companycamQuery.data?.needs_label_sync,
  ]);

  const savePatch = useCallback(
    async (patch: Partial<Job>) => {
      if (!job) return false;
      try {
        await updateRow("jobs", job.id, patch);
        jobQuery.setData({ ...job, ...patch });
        setError(null);
        // An address or lead source can be a lead's last missing detail, and
        // the database ticks Verified when it is (20260928400000).
        const ticks = "address" in patch || "lead_source" in patch;
        await Promise.all([
          data.reload(),
          ticks ? checklistQuery.reload() : undefined,
        ]);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        // The page's error says why. A modal that would otherwise move on
        // from a save that never happened reads the false (ADR 0304).
        return false;
      }
    },
    [job, jobQuery, data, checklistQuery],
  );
  const patchJob = useCallback(
    async (patch: Partial<Job>) => {
      await savePatch(patch);
    },
    [savePatch],
  );

  /**
   * The rep says the customer is paying by check, or takes it back. The
   * reminders on the job wait for it (ADR 0263); the card says until when.
   */
  const checkComing = useCallback(
    async (coming: boolean) => {
      if (!job) return;
      try {
        const patch = await sayCheckIsComing({
          jobId: job.id,
          profileId,
          coming,
        });
        jobQuery.setData({ ...job, ...patch });
        setError(null);
        await data.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [job, jobQuery, data, profileId],
  );

  /** A person turns the job's payment reminders off, or back on (ADR 0302). */
  const remindersOff = useCallback(
    async (off: boolean) => {
      if (!job) return;
      try {
        const patch = await turnPaymentRemindersOff({
          jobId: job.id,
          profileId,
          off,
        });
        jobQuery.setData({ ...job, ...patch });
        setError(null);
        await data.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [job, jobQuery, data, profileId],
  );

  /**
   * A person saying a payment-plan check is in the bank (ADR 0286). It names
   * no slot, so no box settles it, and one Mercury never matches would
   * otherwise stay on its way for good.
   */
  const planPaymentBanked = useCallback(
    async (payment: CustomerPaymentRow) => {
      try {
        await markPlanPaymentBanked(payment);
        setError(null);
        await Promise.all([
          balanceQuery.reload(),
          paymentsQuery.reload(),
          planQuery.reload(),
        ]);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [balanceQuery, paymentsQuery, planQuery],
  );

  /**
   * A rep has been handed over to QuickMeasure to order a report.
   *
   * Written down because money hangs off it: Heely-Brown credits the report
   * against a material order, and the order email can only ask for that credit
   * if something recorded that a report was ordered (ADR 0110). It is the
   * handover that is recorded, not the report — nothing here knows whether GAF
   * sent anything.
   *
   * The first one wins. A rep who orders a second report for the same roof has
   * not made the first one stop existing, and the credit is claimed once.
   */
  const recordQuickMeasureOrder = useCallback(async () => {
    if (!job || job.quickmeasure_ordered_at) return;
    await patchJob({
      quickmeasure_ordered_at: new Date().toISOString(),
      quickmeasure_ordered_by: profileId ?? null,
    });
  }, [job, patchJob, profileId]);

  /**
   * Tick a checklist item.
   *
   * The new status is painted straight into local state rather than refetched.
   * Reloading blanks the panel while the request is in flight, which reads as
   * the whole screen flashing on every click — see the loading guard below.
   * A failure puts the old status back and shows why.
   */
  const setItemStatus = useCallback(
    async (item: JobChecklistItem, status: "pending" | "done" | "na") => {
      const patch = {
        status,
        completed_at: status === "done" ? new Date().toISOString() : null,
        completed_by: status === "done" ? profileId : null,
      };
      const apply = (next: Partial<JobChecklistItem>) =>
        setChecklist((rows) =>
          (rows ?? []).map((row) =>
            row.id === item.id ? { ...row, ...next } : row,
          ),
        );

      apply(patch);
      setError(null);
      setNothingSettled(null);
      let outcome: Awaited<ReturnType<typeof setChecklistItemStatus>>;
      try {
        outcome = await setChecklistItemStatus(item, status, profileId);
      } catch (e) {
        apply({
          status: item.status,
          completed_at: item.completed_at,
          completed_by: item.completed_by,
        });
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      }

      /*
       * First or Final Check moves the money with it (ADR 0159): ticked, the
       * check waiting on that payment is paid; unticked, a check only the box
       * paid waits again. The box stays as the person set it either way.
       */
      const { money, problem } = outcome;
      if (money.settled > 0 || money.putBack > 0) {
        await Promise.all([balanceQuery.reload(), paymentsQuery.reload()]);
      }
      if (problem) {
        setError(problem);
      } else if (money.banked > 0) {
        setError(
          t(
            "The box is unticked, but the check stays paid: Mercury has seen it deposited. Money In is where a bank match is undone.",
          ),
        );
      } else {
        /*
         * A tick that settled nothing on a payment still owed. Jonathon,
         * 2026-09-21: say so, and offer to record the payment right there
         * (ADR 0182). The box stays ticked — a person's tick is their word
         * (ADR 0159) — and nothing moved, so the balance read before the
         * tick is still the balance.
         */
        setNothingSettled(
          tickSettledNothing({
            label: item.label,
            before: item.status,
            after: status,
            settled: money.settled,
            balance: balanceQuery.data ?? null,
          }),
        );
      }
    },
    [profileId, setChecklist, balanceQuery, paymentsQuery],
  );

  /**
   * Tick a box, and when it is First or Final Check Submitted, ask how it was
   * paid.
   *
   * Jonathon, 2026-09-10: the customer gets a receipt "when a rep clicks on
   * payment submitted". A card or bank payment is receipted by Mercury, so the
   * question can be closed with Cancel; a check or cash is recorded and
   * receipted right there. The box stays ticked either way — it is the rep's
   * word that the payment was taken.
   */
  const setStatusAndAskHowPaid = useCallback(
    async (item: JobChecklistItem, status: "pending" | "done" | "na") => {
      await setItemStatus(item, status);
      const slot = slotForSubmittedBox(item.label);
      if (status === "done" && item.status !== "done" && slot) {
        setRecording({ slot });
      }
    },
    [setItemStatus],
  );

  /**
   * Read a receipt over and send it — one that did not go the first time, or
   * one the software drew up and left waiting (ADR 0140).
   *
   * It used to send straight from this button. Jonathon, 2026-09-12: "this
   * email needs to be a prompt we can review before sending it", and a resend
   * is no different from a first send as far as the customer is concerned.
   */
  const resendReceipt = useCallback(async (paymentId: string) => {
    setReviewingReceipt(paymentId);
  }, []);

  /**
   * Submit the Verify form and tick the item.
   *
   * Contact fields, job fields and the note all land before the tick, so a
   * failure leaves the step pending and the rep tries again. Changing the bid
   * type here runs `convert_bid`, which throws the checklists away and builds
   * new ones — the old item id is gone by then, so the fresh Verified row has
   * to be found and ticked instead.
   */
  const verifyJob = useCallback(
    async (item: JobChecklistItem, values: VerifyValues) => {
      if (!job) return;
      const contactPatch = {
        first_name: values.firstName,
        last_name: values.lastName,
        company: values.company,
        cell: values.cell,
        phone: values.phone,
        phone_label: values.phoneLabel,
        email: values.email,
      };
      const jobPatch = {
        lead_source: values.leadSource,
        claim_number: values.claimNumber,
      };

      try {
        await updateRow("contacts", job.contact_id, contactPatch);
        await updateRow("jobs", job.id, jobPatch);

        const bidChanged = values.bidType !== job.bid_type;
        if (bidChanged) {
          const { error: rpcError } = await supabase.rpc("convert_bid", {
            p_job: job.id,
            p_bid: values.bidType,
          });
          if (rpcError) throw new Error(rpcError.message);
        }

        if (values.note) {
          await insertRow("activities", {
            job_id: job.id,
            contact_id: job.contact_id,
            type: "note",
            body: values.note,
            created_by: profileId,
          });
        }

        if (bidChanged) {
          const rebuilt = await fetchChecklist(job.id);
          const verified = rebuilt.find(
            (row) => row.phase === "prospect" && row.label === "Verified",
          );
          if (verified) {
            await updateRow("job_checklist_items", verified.id, {
              status: "done",
              completed_at: new Date().toISOString(),
              completed_by: profileId,
            });
          }
          setChecklist(
            rebuilt.map((row) =>
              row.id === verified?.id ? { ...row, status: "done" } : row,
            ),
          );
        } else {
          await setItemStatus(item, "done");
        }

        // Refetched rather than patched in place. The customer's display name
        // is built from first_name and last_name by a database trigger, so the
        // only place that knows what this job's header should now say is the
        // database.
        setError(null);
        await Promise.all([jobQuery.reload(), data.reload()]);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [job, jobQuery, data, profileId, setChecklist, setItemStatus],
  );

  /**
   * Save a roof layout. The report, if there is one, is stored first — a saved
   * measurement that points at a file which failed to upload would be worse
   * than no measurement at all.
   */
  const saveLayout = useCallback(
    async (input: RoofLayoutInput) => {
      if (!job) return;
      try {
        // A fresh upload, or a report already stored by the wall card when
        // the roof came off the same Hover PDF (see `saveWall`).
        const uploaded = input.pdfFile
          ? await uploadRoofReport(job.id, input.pdfFile)
          : null;
        const pdfPath = uploaded ?? input.pdf_path ?? null;
        const measurement = await insertRow<{ id: string }>(
          "job_measurements",
          {
            job_id: job.id,
            name: input.name,
            average_pitch: input.average_pitch,
            waste_pct: input.waste_pct,
            layers: input.layers,
            source: input.source,
            pdf_path: pdfPath,
            pdf_filename: input.pdf_filename,
            captured_by: profileId,
            ...input.values,
          },
        );
        /*
         * And onto the Docs card. Jonathon, 2026-08-28: "anytime we upload a
         * measurement report it needs to go inside of docs as well."
         *
         * After the measurement, not before: the register points at the
         * measurement it came off, and a row pointing at a measurement that
         * failed to save would be a document belonging to nothing. Keyed on
         * the measurement id, so re-uploading a report replaces its entry
         * rather than listing the roof twice.
         */
        if (uploaded) {
          await registerJobFile({
            jobId: job.id,
            kind: "roof_report",
            title: input.pdf_filename || input.name || "Measurement report",
            bucket: "roof-reports",
            path: uploaded,
            mimeType: "application/pdf",
            sourceTable: "job_measurements",
            sourceId: measurement.id,
            uploadedBy: profileId,
          });
        }
        setError(null);
        await layoutsQuery.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
    [job, profileId, layoutsQuery],
  );

  /**
   * Save a wall measurement, the same way as a roof layout: the Hover report
   * is stored first, then the row, then the file is listed under Docs. When
   * the rep left "also save the roof" ticked, the roof goes on through the
   * roof's own handler, so the one report is filed once and the roof card
   * shows exactly what a roof dropped on it would.
   *
   * Once the wall row is in, nothing after it fails the save. A failure that
   * reached the modal would leave it open with every value still filled in,
   * and a second Submit would insert the same walls again: the siding adds
   * every wall on the job, so the next estimate would order twice the
   * materials and pay the crew twice the squares (#622). So the modal closes
   * and the job screen says what did not save.
   */
  const saveWall = useCallback(
    async (input: WallMeasurementInput) => {
      if (!job) return;
      const messageOf = (e: unknown) =>
        e instanceof Error ? e.message : String(e);
      let pdfPath: string | null;
      let wall: { id: string };
      try {
        pdfPath = input.pdfFile
          ? await uploadRoofReport(job.id, input.pdfFile)
          : null;
        wall = await insertRow<{ id: string }>("job_wall_measurements", {
          job_id: job.id,
          name: input.name,
          waste_pct: input.waste_pct,
          stories: input.stories,
          ...input.values,
          source: input.source,
          pdf_path: pdfPath,
          pdf_filename: input.pdf_filename,
          captured_by: profileId,
        });
      } catch (e) {
        setError(messageOf(e));
        throw e;
      }
      // The walls are in. Each step after this is tried on its own, so one
      // that fails never skips the next: a Docs filing that failed used to
      // drop the roof and leave the wall card showing the old list, which
      // invites adding the walls again by hand (#772).
      let docsFailure: string | null = null;
      let roofFailure: string | null = null;
      if (pdfPath) {
        try {
          await registerJobFile({
            jobId: job.id,
            kind: "roof_report",
            title: input.pdf_filename || input.name || "Hover report",
            bucket: "roof-reports",
            path: pdfPath,
            mimeType: "application/pdf",
            sourceTable: "job_wall_measurements",
            sourceId: wall.id,
            uploadedBy: profileId,
          });
        } catch (e) {
          docsFailure = messageOf(e);
        }
      }
      await wallsQuery.reload();
      if (input.roof) {
        try {
          // The report is already filed above; the roof row points at the
          // same path rather than uploading the file a second time.
          await saveLayout({ ...input.roof, pdfFile: null, pdf_path: pdfPath });
        } catch (e) {
          roofFailure = messageOf(e);
        }
      }
      if (docsFailure && roofFailure) {
        setError(
          t(
            "The walls were saved, but the report was not filed under Docs ({docs}) and the roof was not saved ({roof}). Add the roof on the Roof Measurements card.",
            { docs: docsFailure, roof: roofFailure },
          ),
        );
      } else if (roofFailure) {
        setError(
          t(
            "The walls were saved, but the roof was not: {roof}. Add the roof on the Roof Measurements card.",
            { roof: roofFailure },
          ),
        );
      } else if (docsFailure) {
        setError(
          t(
            "The walls were saved, but the report was not filed under Docs: {docs}.",
            { docs: docsFailure },
          ),
        );
      } else {
        setError(null);
      }
    },
    [job, profileId, wallsQuery, saveLayout],
  );

  const deleteWall = useCallback(
    async (wall: WallMeasurement) => {
      if (
        !window.confirm(
          t('Remove "{name}"?', { name: wall.name || t("these walls") }),
        )
      )
        return;
      try {
        await deleteRow("job_wall_measurements", wall.id);
        setError(null);
        await wallsQuery.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [wallsQuery],
  );

  const deleteLayout = useCallback(
    async (layout: RoofLayout) => {
      if (
        !window.confirm(
          t('Remove "{name}"?', { name: layout.name || t("this layout") }),
        )
      )
        return;
      try {
        await deleteRow("job_measurements", layout.id);
        setError(null);
        await layoutsQuery.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [layoutsQuery],
  );

  /** Attach the signed contract (if there is one) and date the signature. */
  const saveContract = useCallback(
    async (input: SignedContractInput) => {
      if (!job) return;
      try {
        const patch: Partial<Job> = { signed_contract_at: input.signedOn };
        if (input.file) {
          patch.signed_contract_path = await uploadSignedContract(
            job.id,
            input.file,
          );
          patch.signed_contract_filename = input.file.name;
        }
        await updateRow("jobs", job.id, patch);
        jobQuery.setData({ ...job, ...patch });
        setError(null);

        // Signing is what asks the customer for their deposit. The database
        // trigger queued the request the moment the date landed; this works
        // out how much and sends it (migration 20260826130000, ADR 0063).
        // Deliberately not awaited into the error path above: a Mercury
        // hiccup must not make attaching a signed contract look like it
        // failed. The reason lands on the queued row instead.
        void recordDepositAmount({ ...job, ...patch });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
    [job, jobQuery],
  );

  const callRpc = useCallback(
    async (fn: string, args: Record<string, unknown>) => {
      const { error: rpcError } = await supabase.rpc(fn, args);
      if (rpcError) throw new Error(rpcError.message);
    },
    [],
  );

  // Only blank on the very first load. A background reload keeps the last good
  // view on screen instead of tearing the page down and rebuilding it.
  if (jobQuery.loading && !job) return <div className="list" />;
  if (!job) {
    return (
      <div className="list">
        <div className="empty">
          <b>{t("Job not found")}</b>
          {t("It may have been removed.")}
        </div>
      </div>
    );
  }

  const jobCount = data.jobs.filter(
    (j) => j.contact_id === job.contact_id,
  ).length;
  /* The tabs this job actually has. See `shownCards`. */
  const cards = shownCards(
    job,
    partnersEnabled(companyQuery.data?.partners_enabled),
    profile ? isFieldView(profile) : false,
  );
  const card = cards.some((c) => c.name === rememberedCard)
    ? rememberedCard
    : "Overview";
  const active = CARDS.find((c) => c.name === card);

  return (
    <>
      <div className="dhead">
        <div className="thumb">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <h1>
            {job.contact?.full_name ?? t("Job")}
            {/* Badged rather than hidden. A test job is worked through the real
                screens on purpose; what it must not do is turn up in the
                numbers, which is handled in packages/core. */}
            {job.is_test && <span className="testbadge">{t("Test lead")}</span>}
            <WorkingOnNotice
              contactId={job.contact_id}
              others={othersHere}
              customerName={job.contact?.full_name ?? t("this customer")}
            />
          </h1>
          <div className="addr">
            #{job.job_number} · {jobAddress(job) || t("No job address yet")}
          </div>
          {/* Call from here, not from a copied number (ADR 0310). */}
          <div className="dhead-call">
            <CallButton
              number={primaryPhone(job.contact)}
              leadSource={job.lead_source}
              jobId={job.id}
              contactId={job.contact_id}
            />
          </div>
        </div>
        {/* Both of these used to be arrows with a noun — "Customer →" and
            "← All records" — and neither said what pressing it did. They say
            it now. */}
        <span
          className="backlink"
          onClick={() => navigate(`contact=${job.contact_id}`)}
          title={t("Everything this customer has with us")}
        >
          {jobCount > 1
            ? t("Open customer file ({n} jobs)", { n: jobCount })
            : t("Open customer file")}
        </span>
        <span
          className="backlink"
          style={{ marginLeft: 0 }}
          onClick={() => navigate("")}
          title={t("Leave this job and go back to the list you came from")}
        >
          {t("Back to the job list")}
        </span>
      </div>

      <PublicLeadMatchReview
        job={job}
        contacts={data.contacts ?? []}
        onLinked={refresh}
      />

      <nav className="cards">
        {cards.map((c) => (
          <div
            key={c.name}
            className={`card-tab${c.live ? " live" : ""}${
              c.name === card ? " active" : ""
            }`}
            onClick={() => setCard(c.name)}
          >
            <span className="dot" />
            {t(c.name)}
          </div>
        ))}
      </nav>

      <ErrorNote error={error} />

      {nothingSettled && (
        <div className="cowarn" role="status">
          <p style={{ margin: 0 }}>
            {nothingToSettle(nothingSettled)}{" "}
            {t("The box is ticked, and the balance still counts it as owed.")}
          </p>
          <p style={{ margin: "6px 0 0", display: "flex", gap: 6 }}>
            <button
              type="button"
              className="btn-small"
              onClick={() => {
                setRecording({
                  slot: nothingSettled,
                  clearedByBoxSlot: nothingSettled,
                });
                setNothingSettled(null);
              }}
            >
              {t("Record the payment")}
            </button>
            <button
              type="button"
              className="btn-small"
              onClick={() => setNothingSettled(null)}
            >
              {t("Not now")}
            </button>
          </p>
        </div>
      )}

      {askingFor && (
        <PaymentLinkModal
          jobId={jobId}
          suggestedCents={
            balanceQuery.data ? Number(balanceQuery.data.outstanding) : null
          }
          customerName={job.contact?.full_name ?? null}
          // What they already have a live link for, so the confirmation can
          // say when a link would be a second invoice for the same money.
          liveLinkCents={(paymentRequests.data ?? [])
            // Anything the customer is or is about to be asked for: `sent` is
            // a live link, `pending` is a scheduled payment waiting to go out.
            // Warning only about the first missed the commonest case — a
            // deposit not yet sent, and a link raised for the same amount
            // (Josh, 2026-09-13).
            .filter((row) => row.state === "sent" || row.state === "pending")
            .map((row) => Math.round(Number(row.amount_cents ?? 0)))}
          onClose={() => setAskingFor(false)}
          onMade={async () => {
            await balanceQuery.reload();
          }}
        />
      )}

      {reviewingReceipt && (
        <ReceiptReviewModal
          paymentId={reviewingReceipt}
          onClose={() => setReviewingReceipt(null)}
          onSent={async () => {
            await paymentsQuery.reload();
          }}
        />
      )}

      {/* Not until the plan is known: the form leaves the Final out on a plan
          job and picks its starting payment once, so opening it before then
          would offer a $100 check as the whole Final (ADR 0286). */}
      {recording && !planKnown && (
        <Modal
          title={t("Record check or cash")}
          onClose={() => setRecording(null)}
          actions={
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setRecording(null)}
            >
              {t("Close")}
            </button>
          }
        >
          {planQuery.error ? (
            <ErrorNote
              error={t(
                "Could not read this job's payment plan, so a payment cannot be recorded yet: {error}",
                { error: planQuery.error },
              )}
            />
          ) : (
            <p>{t("Reading this job's payment plan…")}</p>
          )}
        </Modal>
      )}

      {recording && planKnown && (
        <RecordPaymentModal
          job={job}
          balance={balanceQuery.data ?? null}
          initialSlot={recording.slot}
          clearedByBoxSlot={recording.clearedByBoxSlot}
          plan={
            planQuery.data
              ? { monthlyCents: planQuery.data.terms.monthlyCents }
              : null
          }
          onClose={() => setRecording(null)}
          onRecorded={async () => {
            // The job too: recording a check ends its "Paying by check" wait
            // in the database, and the Balance card reads that from the job
            // (ADR 0263).
            await Promise.all([
              jobQuery.reload(),
              balanceQuery.reload(),
              paymentsQuery.reload(),
              checklistQuery.reload(),
            ]);
          }}
        />
      )}

      {card === "Overview" && (
        <section className="dpanel show">
          {/* Above the customer's details, because "how much is left" is what
              somebody chasing money opens this page for. It renders nothing at
              all on a job with no price yet. */}
          {balanceQuery.data && (
            <BalanceCard
              balance={balanceQuery.data}
              payments={paymentsQuery.data ?? []}
              stillLive={liveLinksQuery.data ?? []}
              checkExpectedAt={job.check_expected_at ?? null}
              remindersOffAt={job.payment_reminders_off_at ?? null}
              plan={planQuery.data ?? null}
              onRecordPayment={() => setRecording({})}
              onPaymentLink={() => setAskingFor(true)}
              onResendReceipt={(id) => void resendReceipt(id)}
              onCheckComing={(coming) => void checkComing(coming)}
              onRemindersOff={(off) => void remindersOff(off)}
              onPlanPaymentBanked={(payment) => void planPaymentBanked(payment)}
            />
          )}
          {/* Only on the Overview: the Estimate tab says the same thing under
              its own grand total, where the change orders themselves are. */}
          {unsignedQuery.data ? (
            <p className="cowarn">{unsignedQuery.data}</p>
          ) : null}
          {(unsignedWorkQuery.data ?? []).map((found) => (
            <div key={found.estimateId} className="cowarn" role="status">
              <p style={{ margin: 0 }}>{found.warning}</p>
              {profileId ? (
                <p style={{ margin: "6px 0 0" }}>
                  <button
                    type="button"
                    className="btn-small"
                    onClick={() => {
                      void answerDerivedTask({
                        task: unsignedWorkTask(found, job),
                        profileId,
                      })
                        .then(() => unsignedWorkQuery.reload())
                        .catch((e: unknown) =>
                          setError(e instanceof Error ? e.message : String(e)),
                        );
                    }}
                  >
                    Dismiss
                  </button>
                </p>
              ) : null}
            </div>
          ))}
          {termsWarning ? (
            <div className="cowarn" role="status">
              <p style={{ margin: 0 }}>
                {termsWarning.title}{" "}
                {t("Check the payment schedule matches what was promised.")}
              </p>
              {termsWarning.quote ? (
                <p style={{ margin: "6px 0 0" }}>
                  {termsWarning.source ? `${termsWarning.source}: ` : ""}“
                  {termsWarning.quote}”
                </p>
              ) : null}
              <p style={{ margin: "6px 0 0" }}>
                <button
                  type="button"
                  className="btn-small"
                  onClick={() => {
                    void dismissPaymentTermsCheck(jobId)
                      .then(() => termsQuery.reload())
                      .catch((e: unknown) =>
                        setError(e instanceof Error ? e.message : String(e)),
                      );
                  }}
                >
                  {t("This is fine")}
                </button>
              </p>
            </div>
          ) : null}
          {/* A material order line that doesn't show what the messages ask
              for (ADR 0234). A heads-up; renders nothing when there is none. */}
          <MaterialsCheckWarning jobId={jobId} />
          <OverviewPanel
            job={job}
            items={items}
            onPatchJob={patchJob}
            onPatchContact={async (patch) => {
              try {
                await updateRow("contacts", job.contact_id, patch);
                // Refetched, not patched: full_name is rebuilt from the name
                // parts by a database trigger, so the header would otherwise
                // keep saying the old name until the page was reopened. The
                // checklist too: filling in a lead's last detail ticks
                // Verified in the database (20260928400000).
                await Promise.all([
                  jobQuery.reload(),
                  checklistQuery.reload(),
                  data.reload(),
                ]);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
            jobCount={jobCount}
          />
          <ChecklistPanel
            job={job}
            items={items}
            onSetStatus={setStatusAndAskHowPaid}
            onPatchJob={savePatch}
            onVerify={verifyJob}
            onOpenEstimate={() => {
              setCard("Estimate");
              setEstimateVisit((n) => n + 1);
            }}
            layouts={layoutsQuery.data ?? []}
            onSaveLayout={saveLayout}
            onDeleteLayout={deleteLayout}
            onSaveContract={saveContract}
            onRpc={callRpc}
            onRefresh={refresh}
            onQuickMeasureOrdered={recordQuickMeasureOrder}
            onCheckComing={checkComing}
            workOrders={workOrdersQuery.data ?? []}
            onOpenWorkOrders={(orderId) => {
              // "list" rather than null: the estimate card has to know it was
              // sent here on purpose, so it can scroll to the work orders
              // instead of leaving somebody at the top of a long page.
              setWorkOrderFocus(orderId ?? "list");
              setCard("Estimate");
              setEstimateVisit((n) => n + 1);
            }}
            closeout={data.closeouts?.get(job.id) ?? null}
            closeoutOwner={
              (people ?? []).find(
                (p) =>
                  p.id === companyQuery.data?.closeout_profile_id &&
                  p.active !== false,
              ) ?? null
            }
          />
        </section>
      )}

      {card === "Estimate" && (
        <section className="dpanel show wide-card">
          {/* The same card and the same number as the Overview. It is here as
              well because this is the screen where the payment schedule is
              set, and setting one without seeing what has already arrived is
              how the two come apart. */}
          {balanceQuery.data && (
            <BalanceCard
              balance={balanceQuery.data}
              payments={paymentsQuery.data ?? []}
              stillLive={liveLinksQuery.data ?? []}
              checkExpectedAt={job.check_expected_at ?? null}
              remindersOffAt={job.payment_reminders_off_at ?? null}
              plan={planQuery.data ?? null}
              onRecordPayment={() => setRecording({})}
              onResendReceipt={(id) => void resendReceipt(id)}
              onCheckComing={(coming) => void checkComing(coming)}
              onRemindersOff={(off) => void remindersOff(off)}
              onPlanPaymentBanked={(payment) => void planPaymentBanked(payment)}
            />
          )}
          <EstimateCard
            key={estimateVisit}
            job={job}
            onPatchJob={patchJob}
            // Where the checklist sent us, when it did. Cleared once the card
            // has acted on it, so switching back to this tab later does not
            // reopen a work order somebody has finished with.
            workOrderFocus={workOrderFocus}
            onWorkOrderFocused={() => setWorkOrderFocus(null)}
            onOpenMeasurements={() => setCard("Roof Measurements")}
            onEstimateCreated={async () => {
              const step = items.find(
                (i) => i.phase === "prospect" && i.label === "Estimate",
              );
              if (step && step.status !== "done") {
                await setItemStatus(step, "done");
              }
            }}
            // "Estimate Sent" is ticked by the database the moment a document
            // is presented, so there is nothing to write here — only the
            // checklist to fetch again, which is what moves the stage.
            onPresented={refresh}
          />
        </section>
      )}

      {card === "Insurance Estimate" && (
        <section className="dpanel show wide-card">
          <InsuranceEstimateCard job={job} />
        </section>
      )}

      {card === "Docs" && (
        <section className="dpanel show">
          <DocsPanel job={job} />
          {/* Storm history sits with the other things that leave the building
              for this job, because that is what it is — see ADR 0043. */}
          <StormReportPanel job={job} />
        </section>
      )}

      {card === "Payments" && (
        <section className="dpanel show">
          <PaymentsCard
            rows={historyQuery.data ?? []}
            canRemove={isAdmin}
            onRecord={() => setRecording({})}
            onInTheBank={async (row) => {
              if ("linkId" in row) {
                await markLinkInTheBank(row.linkId, profileId);
              } else {
                await markPaymentInTheBank(row.recordedId, profileId);
              }
              await Promise.all([
                balanceQuery.reload(),
                paymentsQuery.reload(),
                checklistQuery.reload(),
              ]);
            }}
            onRemove={async (id, reason) => {
              await removeCustomerPayment(id, reason);
              await Promise.all([
                balanceQuery.reload(),
                paymentsQuery.reload(),
              ]);
            }}
          />
        </section>
      )}

      {card === "Notes" && (
        <section className="dpanel show">
          <NotesPanel job={job} />
        </section>
      )}

      {card === "Activity" && (
        <section className="dpanel show">
          <ActivityPanel jobId={job.id} />
        </section>
      )}

      {card === "Emails" && (
        <section className="dpanel show">
          <EmailPanel job={job} />
        </section>
      )}

      {card === "Partners" &&
        partnersEnabled(companyQuery.data?.partners_enabled) && (
          <section className="dpanel show">
            <PartnersPanel job={job} />
          </section>
        )}

      {card === "Texts" && (
        <section className="dpanel show">
          <TextsPanel job={job} />
        </section>
      )}

      {card === "Roof Measurements" && (
        <section className="dpanel show">
          <RoofMeasurementsCard
            job={job}
            layouts={layoutsQuery.data ?? []}
            onSave={saveLayout}
            onDelete={deleteLayout}
            onQuickMeasureOrdered={recordQuickMeasureOrder}
          />
          <WallMeasurementsCard
            job={job}
            walls={wallsQuery.data ?? []}
            layouts={layoutsQuery.data ?? []}
            onSave={saveWall}
            onDelete={deleteWall}
          />
        </section>
      )}

      {card === "Photos" && job && (
        <section className="dpanel show">
          <CompanyCamCard
            job={job}
            link={companycamQuery.data ?? null}
            onLinked={async () => {
              await Promise.all([jobQuery.reload(), companycamQuery.reload()]);
            }}
          />
        </section>
      )}

      {card === "Conversations" && (
        <section className="dpanel show">
          <ConversationsCard
            jobId={jobId}
            repId={profileId}
            conversations={conversationsQuery.data ?? []}
            questions={askedQuery.data ?? []}
            onImported={async () => {
              await conversationsQuery.reload();
            }}
            onAsked={async () => {
              await askedQuery.reload();
            }}
          />
        </section>
      )}

      {active && !active.live && (
        <section className="dpanel show">
          <div className="block">
            <div className="stub">
              <b>{t(active.name)}</b>
              {t(active.stub ?? "Planned, not wired up in v1.")}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
